/** @jsx jsx */
import { DndProvider } from 'react-dnd';
import Backend from 'react-dnd-html5-backend';
import DraggableContainer from 'components/multiselect/draggable';
import { Button } from '@blueprintjs/core';
import { Icon } from 'components/icon';
import { v4 as uuidv4 } from 'uuid';

import { css, jsx } from '@emotion/react';
import { Colors, ColorAliases, Variables } from 'styles/variables';
import update from 'immutability-helper';
import { useCallback, useState } from 'react';
import { multiselectStyles } from 'components/multiselect/styles';

const convertToArrayOfObjects = array => array.map(text => ({id: uuidv4(), text}));
export default function({ items, initialSelected = items, minimumItems=1, onSubmit}) {

	const initialUnselected = items.filter(item => !initialSelected.includes(item));
	const itemsAsObjects = convertToArrayOfObjects(items);
	const initialSelectedAsObjects = convertToArrayOfObjects(initialSelected);
	const initialUnselectedAsObjects = convertToArrayOfObjects(initialUnselected);

	const [selected, setSelected] = useState(initialSelectedAsObjects);

	const [unselected, setUnselected] = useState(initialUnselectedAsObjects);

	const moveSelected = useCallback(
		(draggingIndex, hoveringIndex) => {
			const dragItem = selected[draggingIndex];
			setSelected(
				update(selected, {
					$splice: [
						[draggingIndex, 1],
						[hoveringIndex, 0, dragItem],
					],
				})
			);
		},
		[selected]
	);

	const moveUnselected = useCallback(
		(draggingIndex, hoveringIndex) => {
			const dragItem = unselected[draggingIndex];
			setUnselected(
				update(unselected, {
					$splice: [
						[draggingIndex, 1],
						[hoveringIndex, 0, dragItem],
					],
				})
			);
		},
		[unselected]
	);

	const handleSelect = newValue => {
		setSelected(selected => [...selected, newValue]);
		setUnselected(unselected.filter(item => item !== newValue));
	};

	const handleUnselect = newValue => {
		if (selected.length <= minimumItems) return;
		setUnselected(selected => [...selected, newValue]);
		setSelected(selected.filter(item => item !== newValue));
	};

	const handleSelectAll = () => {
		if (itemsAsObjects !== selected) {
			setSelected(itemsAsObjects);
			setUnselected([]);
		}
	};

	// Don't want to allow the user to unselect all (causes system to crash),
	//    this doesn't do anything if the items are at n (the minimium number)
	//    if there are more than the minimum number, select the first n
	const handleUnselectAll = () => {
		if (selected.length <= minimumItems) return;
		setSelected(itemsAsObjects.slice(0, minimumItems))
		setUnselected(itemsAsObjects.slice(minimumItems))
	};

	const handleReset = () => {
		setSelected(initialSelectedAsObjects);
		setUnselected(initialUnselectedAsObjects);
	};

	const handleClose = () => {
		handleSelectAll();
		handleSubmit();
	};

	const handleSubmit = () => {
		const selectedToReturn = selected.map(item => item.text);
		const unselectedToReturn = unselected.map(item => item.text);
		const returnObject = { selected: selectedToReturn, unselected: unselectedToReturn };
		onSubmit(returnObject);
	};

	const { itemTitle } = multiselectStyles;
	return (
		<div css={containerCss}>
			<div css={selectionColumnCss}>
				<h5 css={itemTitle}>
					<div>Unselected</div>
				</h5>
				<DndProvider backend={Backend}>
					<DraggableContainer items={unselected} handleReorder={moveUnselected} toggleSelected={handleSelect} />
				</DndProvider>
			</div>
			<div css={buttonColumnCss}>
				<Button minimal={true} onClick={handleSubmit} title="Confirm">
					<Icon iconSize={20} icon="confirm"/>
				</Button>
				<Button minimal={true} onClick={handleReset} title="Reset">
					<Icon iconSize={20} icon="reset"/>
				</Button>
				<Button minimal={true} onClick={handleSelectAll} title="Select All">
					<Icon iconSize={20} icon="double-chevron-right"/>
				</Button>
				<Button minimal={true} onClick={handleUnselectAll} title="Deselect All">
					<Icon iconSize={20} icon="double-chevron-left"/>
				</Button>
			</div>
			<div css={selectionColumnCss}>
				<h5 css={itemTitle}>
					<div>Selected</div>
				</h5>
				<DndProvider backend={Backend}>
					<DraggableContainer
						items={selected}
						handleReorder={moveSelected}
						toggleSelected={handleUnselect}
					></DraggableContainer>
				</DndProvider>
			</div>
		</div>
	)
}

const containerCss = css`
	display: flex;
	border: 1px solid #CED9E0; //TODO use colorAliases ?LIGHT_GRAY1
	border-radius: ${Variables.PT_BORDER_RADIUS};
`;
const selectionColumnCss = css`
	flex: 1
`;
const buttonColumnCss = css`
	display: flex;
	justify-content: center;
	align-items: center;
	flex-direction: column;
	flex-shrink: 0;
	flex-basis: 84px;
	background: ${Colors.LIGHT_GRAY1};
	color: ${ColorAliases.PT_ICON_COLOR};
	& > * {
		margin: 7px;
		cursor: pointer;
		padding: 5px;
	}
`;
